import {
	ISimpleAutoCompleteSearchFieldEvent,
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '@WebComponents/autocomplete/SimpleAutoCompleteSearchField';
import { css } from 'aphrodite';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import * as Api from '../../../../viewmodels/AppViewModels';
import { Button } from '../../../components-v2/Button';
import { Modal } from '../../Modal';
import { styleSheet } from './styles';

export interface ITestEmailModalProps {
	isOpen: boolean;
	notice?: string;
	onRequestClose: (result?: ITestEmailResult, canceled?: boolean) => void;
	onSendTestEmail: (selectedUserId: string) => Promise<Api.IOperationResult<Api.ISendEmailResponse>>;
}

export interface ITestEmailResult {
	success: boolean;
	selectedUserId?: string;
}

export const TestEmailModal: React.FC<ITestEmailModalProps> = ({ isOpen, notice, onRequestClose, onSendTestEmail }) => {
	const mUserSession = useUserSession();
	const [selectedUser, setSelectedUser] = useState<Api.IUser | null>(mUserSession.user);
	const [isSending, setIsSending] = useState(false);
	// This is to make sure the display of the AutoCompleteSearchField is consistent and shows the user who is selected by default.
	const [initialSearchQuery, setInitialSearchQuery] = useState<string>(
		`${mUserSession.user.firstName} ${mUserSession.user.lastName}`
	);
	const { logApiError } = useEventLogging('TestEmailModal');
	const errorMessages = useErrorMessages();

	const handleSend = async () => {
		if (!selectedUser) {
			errorMessages.push({ messages: ['Please select a user to send the test email to.'] });
			return;
		}

		setIsSending(true);

		try {
			const result = await onSendTestEmail(selectedUser.contactId);
			if (result.success) {
				onRequestClose({ success: true, selectedUserId: selectedUser.id }, false);
			} else {
				errorMessages.push({ messages: [result.systemMessage || 'Failed to send test email. Please try again.'] });
			}
		} catch (err) {
			const apiError = Api.asApiError(err);
			logApiError('TestEmailSend-Error', apiError);
			errorMessages.push({ messages: [apiError.systemMessage || 'Failed to send test email. Please try again.'] });
		} finally {
			setIsSending(false);
		}
	};

	const handleCancel = useCallback(() => {
		onRequestClose(null, true);
	}, [onRequestClose]);

	const onEmployeeSelected = (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<Api.IUser>) => {
		setSelectedUser(e.selection);
		setInitialSearchQuery(`${e.selection.firstName} ${e.selection.lastName}`);
	};

	const clearSelectedEmployee = () => {
		setSelectedUser(null);
		setInitialSearchQuery('');
	};
	const onSelectAnEmployeeChange = (e: ISimpleAutoCompleteSearchFieldEvent<React.KeyboardEvent<HTMLInputElement>>) => {
		if (e.sourceEvent && e.sourceEvent.keyCode !== 13) {
			clearSelectedEmployee();
		}
	};
	const onCreateAutoCompleteViewModel = (
		_: Api.ResourceAutoCompleteViewModelType,
		suggestedViewModel: Api.ResourceAutoCompleteViewModel
	) => {
		suggestedViewModel.addParam({ includeDeactivated: false });
		return suggestedViewModel;
	};
	return (
		<Modal isOpen={isOpen} onRequestClose={handleCancel} shouldCloseOnOverlayClick={true} useDefaultHeader={false}>
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.header)}>Send Test Email</div>

				<div className={css(styleSheet.content)}>
					<div>
						<div className={css(styleSheet.label)}>Send to:</div>
						<SimpleAutoCompleteSearchField
							onClear={clearSelectedEmployee}
							initialSearchQuery={initialSearchQuery}
							// @ts-expect-error - 'onItemSelected' is meant to support a large number of types and as such the signature of the method is strictly matched. This error is ignored everywhere it's used.
							onItemSelected={onEmployeeSelected}
							onKeyDown={onSelectAnEmployeeChange}
							placeholder='Search employee'
							style={styleSheet.searchEmployeeBox}
							type={Api.ResourceAutoCompleteViewModelType.User}
							onCreateAutoCompleteViewModel={onCreateAutoCompleteViewModel}
						/>
					</div>
					{notice ? <p className={css(styleSheet.notice)}>{notice}</p> : null}
				</div>

				<div className={css(styleSheet.actions)}>
					<Button variant='secondary' onClick={handleCancel} disabled={isSending}>
						Cancel
					</Button>
					<Button onClick={handleSend} disabled={isSending || !selectedUser}>
						{isSending ? 'Sending...' : 'Send Test Email'}
					</Button>
				</div>
			</div>
		</Modal>
	);
};
